/**
 * Streams chat completions from the Cloudflare Worker proxy `/chat`
 * route. Mirrors the request shape used by the native `ClaudeAPI.swift`
 * — the worker forwards directly to `api.anthropic.com/v1/messages`, so
 * we send Anthropic's native message format.
 *
 * Streaming is SSE: we parse `event:` / `data:` lines ourselves rather
 * than pull in an EventSource polyfill, because the browser `EventSource`
 * cannot POST a body.
 *
 * The stream parser handles two kinds of content blocks:
 *   - text         → text_delta chunks → onTextChunk callback
 *   - tool_use     → input_json_delta partials → onToolUse on stop
 *
 * For history validity, any assistant message in the conversation that
 * contained tool_use blocks must be followed by a user message starting
 * with matching tool_result blocks. Studio tools are fire-and-forget
 * side effects — there's no real result to return — so we synthesize a
 * minimal `"ok"` tool_result for each pending id when building the
 * outgoing user message.
 */

import type {
  AnthropicContentBlock,
  AnthropicTool,
  ConversationMessage,
  PageContext,
  ToolUseBlock,
} from "../types";

const DEFAULT_MODEL = "claude-sonnet-4-6";
/**
 * Per-turn token budget. Bumped from 1024 — that ceiling caused
 * Claude to give up partway through multi-tool requests like "draft
 * costumes for all five characters" (the bird would type three and
 * stop). 4096 covers a comfortable burst of tool calls + narration
 * for a single turn without paying for headroom we'd never use.
 */
const DEFAULT_MAX_TOKENS = 4096;

export interface ClaudeStreamCallbacks {
  onTextChunk: (chunkText: string) => void;
  /**
   * Fired once per `tool_use` block as soon as its `content_block_stop`
   * event arrives. Side-effect handlers run client-side; the prototype
   * does not roundtrip a real tool_result payload back in the same turn.
   */
  onToolUse?: (block: ToolUseBlock) => void;
  /**
   * Final assistant-message text and any completed tool_use blocks.
   * Caller decides how to persist these into conversation history.
   */
  onComplete: (finalFullText: string, toolUses: ToolUseBlock[]) => void;
  onError: (error: Error) => void;
}

export interface ClaudeStreamRequest {
  systemPrompt: string;
  history: readonly ConversationMessage[];
  userText: string;
  pageContext: PageContext;
  model?: string;
  /**
   * Whether to allow Claude to use Anthropic's server-side web search
   * tool. When true the request includes `web_search` in the `tools`
   * array; Claude decides per-turn whether to invoke it (so leaving
   * this on doesn't force a search on every turn — only the ones
   * that need fresh info).
   */
  webSearchEnabled?: boolean;
  /**
   * Custom tools defined by the host page (e.g. studio's add_character).
   * Merged into the same `tools` array as web_search; Claude picks
   * whichever (if any) per turn.
   */
  customTools?: readonly AnthropicTool[];
}

const WEB_SEARCH_MAX_USES_PER_TURN = 3;

export class ClaudeClient {
  private readonly workerBaseUrl: string;
  private activeAbortController: AbortController | null = null;

  constructor(workerBaseUrl: string) {
    this.workerBaseUrl = workerBaseUrl.replace(/\/$/, "");
  }

  /** Cancels any in-flight stream. Safe to call from anywhere. */
  cancelInFlight(): void {
    if (this.activeAbortController) {
      this.activeAbortController.abort();
      this.activeAbortController = null;
    }
  }

  async streamResponse(
    request: ClaudeStreamRequest,
    callbacks: ClaudeStreamCallbacks,
  ): Promise<void> {
    this.cancelInFlight();
    const abortController = new AbortController();
    this.activeAbortController = abortController;

    const messages = buildRequestMessages(
      request.history,
      request.userText,
      request.pageContext,
    );

    const tools: unknown[] = [];
    if (request.webSearchEnabled) {
      tools.push({
        type: "web_search_20250305",
        name: "web_search",
        max_uses: WEB_SEARCH_MAX_USES_PER_TURN,
      });
    }
    if (request.customTools && request.customTools.length > 0) {
      tools.push(...request.customTools);
    }

    const body: Record<string, unknown> = {
      model: request.model ?? DEFAULT_MODEL,
      max_tokens: DEFAULT_MAX_TOKENS,
      stream: true,
      system: request.systemPrompt,
      messages,
    };

    if (tools.length > 0) {
      body.tools = tools;
    }

    let response: Response;
    try {
      response = await fetch(`${this.workerBaseUrl}/chat`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
        signal: abortController.signal,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;
      callbacks.onError(
        new Error(`Network error calling /chat: ${(error as Error).message}`),
      );
      return;
    }

    if (!response.ok || !response.body) {
      const errorText = await response.text().catch(() => "");
      callbacks.onError(
        new Error(`/chat returned ${response.status}: ${errorText.slice(0, 400)}`),
      );
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = "";
    let accumulatedText = "";
    /**
     * Per-content-block partial state. Keyed by Anthropic's `index`
     * field (an integer assigned per block in the response). We only
     * track tool_use blocks here; text blocks just accumulate into
     * `accumulatedText`.
     */
    const inFlightToolUseBlocks = new Map<
      number,
      { id: string; name: string; partialJson: string }
    >();
    const completedToolUseBlocks: ToolUseBlock[] = [];

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by blank lines.
        let frameBoundary = sseBuffer.indexOf("\n\n");
        while (frameBoundary !== -1) {
          const frame = sseBuffer.slice(0, frameBoundary);
          sseBuffer = sseBuffer.slice(frameBoundary + 2);
          processFrame(frame, {
            onText: (chunk) => {
              accumulatedText += chunk;
              callbacks.onTextChunk(chunk);
            },
            onToolUseStart: (index, id, name) => {
              inFlightToolUseBlocks.set(index, { id, name, partialJson: "" });
            },
            onToolUseInputDelta: (index, partial) => {
              const block = inFlightToolUseBlocks.get(index);
              if (block) block.partialJson += partial;
            },
            onContentBlockStop: (index) => {
              const block = inFlightToolUseBlocks.get(index);
              if (!block) return;
              inFlightToolUseBlocks.delete(index);
              const parsedInput = parseToolInputJson(block.partialJson);
              const completed: ToolUseBlock = {
                id: block.id,
                name: block.name,
                input: parsedInput,
              };
              completedToolUseBlocks.push(completed);
              callbacks.onToolUse?.(completed);
            },
          });
          frameBoundary = sseBuffer.indexOf("\n\n");
        }
      }
      callbacks.onComplete(accumulatedText, completedToolUseBlocks);
    } catch (error) {
      if (abortController.signal.aborted) return;
      callbacks.onError(
        new Error(`Error reading SSE stream: ${(error as Error).message}`),
      );
    } finally {
      if (this.activeAbortController === abortController) {
        this.activeAbortController = null;
      }
    }
  }
}

/**
 * Build the `messages` array for the outgoing request.
 *
 * Two history-validity rules drive the shape:
 *   1. Any user message that follows an assistant message with
 *      `tool_use` blocks must START with a `tool_result` block for
 *      every such id. Anthropic 400s otherwise.
 *   2. The new user turn (the one being added right now) is no
 *      different — if the most recent assistant message had tool_uses,
 *      the new user message gets the same tool_result preface.
 *
 * Studio tools are fire-and-forget side effects, so the result content
 * is just a no-op `"ok"` string — Claude only needs the `tool_use_id`
 * to satisfy the API contract.
 */
export function buildRequestMessages(
  history: readonly ConversationMessage[],
  newUserText: string,
  pageContext: PageContext,
): unknown[] {
  const out: unknown[] = [];

  for (let messageIndex = 0; messageIndex < history.length; messageIndex++) {
    const message = history[messageIndex];

    if (message.role === "assistant") {
      out.push({ role: "assistant", content: message.content });

      // Invariant: an assistant message with `tool_use` blocks MUST be
      // immediately followed by a user message carrying matching
      // `tool_result` blocks, or Anthropic 400s. The two common cases —
      // the next history message is a user turn (prefaced below) and the
      // tool_use being the very last history message (handled by the new
      // user turn at the end) — are covered elsewhere. The gap this
      // guards is an `assistant(tool_use)` directly followed by ANOTHER
      // assistant message (e.g. a report-back turn whose synthetic user
      // prompt wasn't persisted). Splice in a standalone tool_result
      // user message so the contract holds regardless of how history
      // was assembled upstream.
      const toolUseIds = extractToolUseIds(message);
      const nextMessage = history[messageIndex + 1];
      if (toolUseIds.length > 0 && nextMessage && nextMessage.role !== "user") {
        out.push({ role: "user", content: toolResultBlocksFor(toolUseIds) });
      }
      continue;
    }

    // User message — possibly needs tool_result preface based on the
    // assistant message immediately before it.
    const previousMessage = history[messageIndex - 1];
    const toolUseIdsToAcknowledge = extractToolUseIds(previousMessage);

    if (toolUseIdsToAcknowledge.length === 0) {
      out.push({ role: "user", content: message.content });
      continue;
    }

    const baseUserContent: AnthropicContentBlock[] =
      typeof message.content === "string"
        ? [{ type: "text", text: message.content }]
        : message.content;
    out.push({
      role: "user",
      content: [...toolResultBlocksFor(toolUseIdsToAcknowledge), ...baseUserContent],
    });
  }

  // The new user turn being appended right now.
  const lastHistoryMessage = history[history.length - 1];
  const pendingToolUseIds = extractToolUseIds(lastHistoryMessage);
  const newUserContent: AnthropicContentBlock[] = [
    ...toolResultBlocksFor(pendingToolUseIds),
    {
      type: "image",
      source: {
        type: "base64",
        media_type: pageContext.screenshotMediaType,
        data: pageContext.screenshotBase64,
      },
    },
    {
      type: "text",
      text:
        `Viewport screenshot (${pageContext.viewportWidth}x${pageContext.viewportHeight} pixels).\n\n` +
        `Page summary:\n${pageContext.domSummary}\n\n` +
        `User said:\n${newUserText}`,
    },
  ];
  out.push({ role: "user", content: newUserContent });

  return out;
}

/**
 * Build the no-op `tool_result` blocks that acknowledge a set of
 * `tool_use` ids. Studio tools are fire-and-forget side effects, so the
 * result payload is a minimal `"ok"` — Claude only needs the matching
 * `tool_use_id` to satisfy the API contract.
 */
function toolResultBlocksFor(toolUseIds: readonly string[]): AnthropicContentBlock[] {
  return toolUseIds.map((toolUseId) => ({
    type: "tool_result",
    tool_use_id: toolUseId,
    content: "ok",
  }));
}

/**
 * Pull tool_use ids out of an assistant message's content array, if
 * any. Returns an empty array for plain-text messages or when the
 * argument is absent.
 */
export function extractToolUseIds(
  message: ConversationMessage | undefined,
): string[] {
  if (!message || message.role !== "assistant") return [];
  if (!Array.isArray(message.content)) return [];
  const ids: string[] = [];
  for (const block of message.content) {
    if (
      block &&
      typeof block === "object" &&
      "type" in block &&
      block.type === "tool_use" &&
      "id" in block &&
      typeof block.id === "string"
    ) {
      ids.push(block.id);
    }
  }
  return ids;
}

/**
 * Parse the accumulated `input_json_delta` payload for a single
 * tool_use block. Anthropic streams a STRING that is intended to be
 * concatenated and parsed as one JSON value — empty string means the
 * tool was called with no arguments (input is `{}`).
 */
function parseToolInputJson(accumulatedPartialJson: string): unknown {
  const trimmed = accumulatedPartialJson.trim();
  if (trimmed.length === 0) return {};
  try {
    return JSON.parse(trimmed);
  } catch (error) {
    console.warn(
      "[koko] failed to parse tool_use input_json (using empty input):",
      trimmed,
      error,
    );
    return {};
  }
}

interface FrameHandlers {
  onText: (chunk: string) => void;
  onToolUseStart: (index: number, id: string, name: string) => void;
  onToolUseInputDelta: (index: number, partial: string) => void;
  onContentBlockStop: (index: number) => void;
}

/**
 * Process a single SSE frame. Frame may have multiple `data:` lines but
 * Anthropic always uses one. We dispatch on the parsed JSON `type`
 * field to drive both text streaming and tool_use accumulation.
 */
function processFrame(frame: string, handlers: FrameHandlers): void {
  const lines = frame.split("\n");
  for (const line of lines) {
    if (!line.startsWith("data:")) continue;
    const payload = line.slice(5).trim();
    if (payload.length === 0 || payload === "[DONE]") continue;

    let parsed: {
      type?: string;
      index?: number;
      content_block?: {
        type?: string;
        id?: string;
        name?: string;
      };
      delta?: {
        type?: string;
        text?: string;
        partial_json?: string;
      };
    };
    try {
      parsed = JSON.parse(payload);
    } catch {
      // Malformed frame; ignore and keep streaming.
      continue;
    }

    if (parsed.type === "content_block_start") {
      const blockIndex = parsed.index;
      const block = parsed.content_block;
      if (
        typeof blockIndex === "number" &&
        block?.type === "tool_use" &&
        typeof block.id === "string" &&
        typeof block.name === "string"
      ) {
        handlers.onToolUseStart(blockIndex, block.id, block.name);
      }
      continue;
    }

    if (parsed.type === "content_block_delta") {
      const blockIndex = parsed.index;
      const delta = parsed.delta;
      if (delta?.type === "text_delta" && typeof delta.text === "string") {
        handlers.onText(delta.text);
      } else if (
        delta?.type === "input_json_delta" &&
        typeof delta.partial_json === "string" &&
        typeof blockIndex === "number"
      ) {
        handlers.onToolUseInputDelta(blockIndex, delta.partial_json);
      }
      continue;
    }

    if (parsed.type === "content_block_stop") {
      if (typeof parsed.index === "number") {
        handlers.onContentBlockStop(parsed.index);
      }
      continue;
    }
  }
}
